import { Brand } from '@/models/Brand';
import { Category } from '@/models/Category';
import { Product as ProductModel, ProductIncludes } from '@/models/Product';
import { Supplier } from '@/models/Supplier';
import ErrorHandler from '@/support/ErrorHandler';
import UploadProductImageDialog from '@/views/Products/UploadProductImageDialog/UploadProductImageDialog';
import { AxiosError } from 'axios';
import { cloneDeep, debounce } from 'lodash';
import { Component, Vue, Watch } from 'vue-property-decorator';
import ProductImages from './Tabs/ProductImages/ProductImages';
import ProductVariants from './Tabs/ProductVariants/ProductVariants';

@Component<Products>({
  components: {
    ProductImages,
    ProductVariants,
    UploadProductImageDialog
  }
})
export default class Products extends Vue {
  //#region @Props
  /**
  * ie.
  * @Prop()
  * protected user!: User
  */
  //#endregion


  //#region @Refs
  /**
  * ie.
  * @Ref()
  * readonly anotherComponent!: AnotherComponent
  */
  //#endregion


  //#region Class properties
  /**
  * ie.
  * protected isLoading = true;
  * * protected company: Company | null = null;
  */

  protected isLoading = false;

  protected isUpdating = false;
  protected isEditing = false;
  protected product: ProductModel | null = null;
  protected productClone: ProductModel | null = null;

  protected isUploadingImages = false;

  protected activeTab = 'variants';

  protected categories: Category[] | null = null;
  protected categorySearch: string = "";
  protected searchCategoryDebounce: Function = debounce((searchInput: string) => this.getCategories(searchInput), 400);
  //#endregion


  //#region Lifecycle Hooks / Init
  /**
  * ie.
  * protected mounted(): void {
  *  console.log('MyComponent has Mounted');
  * }
  */

  protected mounted(): void {
    this.initialize();
  }
  //#endregion


  //#region Class methods
  /**
  * ie.
  * protected toggleLoading(): void {
  *  this.isLoading = ! this.isLoading;
  * }
  */

  protected async initialize(): Promise<void> {
    this.isLoading = true;
    Promise.all([
      await this.getProduct(),
    ]);
    this.isLoading = false;
  }

  protected resetProduct() {
    this.product = cloneDeep(this.productClone);
    this.productClone = null;
  }

  protected edit() {
    this.productClone = cloneDeep(this.product);
    this.isEditing = true;
  }

  protected onImagesUploaded(): void {
    setTimeout(() => {
      this.initialize();
    }, 1000);
  }

  //#endregion


  //#region Async methods
  /**
  * ie.
  * protected async fetchUserCompany(): Promise<void> {
  *  this.company = await new Company().filter({user: this.user.id}).all();
  * }
  */

  protected async getProduct(): Promise<void> {
    try {
      this.product = await new ProductModel()
        .include([
          ProductIncludes.BRAND,
          ProductIncludes.IMAGES,
          ProductIncludes.CATEGORIES,
          ProductIncludes.SUPPLIER,
          ProductIncludes.METADATA
        ])
        .find(this.$route.params.id)

    } catch (error) {
      ErrorHandler.network(error);
    }
  }

  protected async getCategories(filter: string = ""): Promise<void> {
    await new Category()
      .filter("search", filter)
      .limit(999) // so we can also search inside all the sub-items
      .all()
      .then((categories: Category[]) => {
        let parsedCategories: Category[] = [];
        categories.forEach((category: any) => {
          parsedCategories.push(category);
          if (category.sub_categories && category.sub_categories.length > 1) {
            category.sub_categories.forEach((subColor: Category) => {
              parsedCategories.push(subColor);
            });
          }
        });

        this.categories = parsedCategories;
      })
      .catch((error: AxiosError) => {});
  }

  protected async update() {
    if (!this.product) {
      return;
    }
    this.isUpdating = true;

    try {
      this.product = await this.product
        .include([
          ProductIncludes.BRAND,
          ProductIncludes.IMAGES,
          ProductIncludes.CATEGORIES,
          ProductIncludes.SUPPLIER,
          ProductIncludes.METADATA
        ])
        .update(this.updatePayload);
    } catch (error) {

    }
    this.isUpdating = false;
    this.isEditing = false;
  }
  //#endregion


  //#region Getters & Setters
  /**
  * ie.
  * protected get companyName(): string {
  *  return this?.company.name || 'N/A';
  * }
  */
  protected get tabs(): Tab[] {
    return [
      {
        title: 'Product variants',
        key: 'variants',
        component: 'ProductVariants'
      },
      {
        title: 'Product images',
        key: 'images',
        component: 'ProductImages'
      },
    ]
  }

  protected get supplierModel(): Supplier {
    return new Supplier();
  }

  protected get brandModel(): Brand {
    return new Brand();
  }

  protected get updatePayload() {
    if (!this.product) {
      return {};
    }

    return {
      shopify_product_id: this.product.shopify_product_id,
      supplier_style_name: this.product.supplier_style_name,
      supplier_style_code: this.product.supplier_style_code,
      name: this.product.name,
      description: this.product.description,
      supplier: this.product.supplier && this.product.supplier.id ? this.product.supplier.id : this.product.supplier,
      brand: this.product.brand && this.product.brand.id ? this.product.brand.id : this.product.brand,
      categories: this.product.categories ? this.product.categories.map(category => {
        return category.id;
      }) : [],
    };
  }
  //#endregion


  //#region @Watchers
  /**
  * ie.
  * @Watch('company')
  * protected companyChanged() {
  *  console.log('Company Changed') ;
  * }
  */

  @Watch("categorySearch")
  protected categorySearchChanged(category: string) {
    this.searchCategoryDebounce(category);
  }

  //#endregion
}

interface Tab {
  key: string;
  title: string;
  component: string;
}
